/**
 * Product Comparison
 * Header slots hold the theme's own product cards (pre-rendered in Liquid, moved in by entry id).
 * Spec rows and the picker are driven from the metaobject JSON.
 * Rule: a scooter is never in two columns at once.
 */

function convertKmToMi(kmValue) {
	const miValue = kmValue * 0.621371;
	if (Math.floor(miValue * 100) % 100 > 85) return Math.ceil(miValue).toFixed(0);
	if (Math.floor(miValue * 100) % 100 > 5) return Math.floor(miValue).toFixed(0) + '.5';
	return miValue % 1 === 0 ? miValue.toFixed(0) : miValue.toFixed(1);
}

function isValid(v) { return typeof v === 'number' && Number.isFinite(v) && v >= 0; }

// Scores are out of 10 in the metaobject; shown as a 10-segment bar only
function scoreBar(score) {
	const value = Math.max(0, Math.min(10, Math.round(score)));
	let html = '<span class="product-comparison__bar-score" role="img" aria-label="' + value + '/10">';
	for (let i = 0; i < 10; i++) html += i < value ? '<span class="is-on"></span>' : '<span></span>';
	return html + '</span>';
}

function escapeHtml(str) {
	const div = document.createElement('div');
	div.textContent = str;
	return div.innerHTML;
}

document.addEventListener('DOMContentLoaded', () => {
	const container = document.querySelector('[js-comparison="container"]');
	const dataEl = document.querySelector('[js-comparison="dataTable"]');
	const stringsEl = document.querySelector('[js-comparison="strings"]');
	if (!container || !dataEl || !stringsEl) return;

	const data = JSON.parse(dataEl.textContent);
	// Shopify's t filter HTML-escapes ("&" arrives as "&amp;"), so decode every string once
	const decoder = document.createElement('textarea');
	const t = JSON.parse(stringsEl.textContent, (key, value) => {
		if (typeof value !== 'string') return value;
		decoder.innerHTML = value;
		return decoder.value;
	});
	const entries = data.entries.filter((e) => e.product && e.product.id);
	const byId = new Map(entries.map((e) => [e.id, e]));

	const root = container.querySelector('.product-comparison');
	const slotEls = [...container.querySelectorAll('[js-comparison="slot"]')];
	const slotsRow = container.querySelector('[js-comparison="slots"]');
	const rows = [...container.querySelectorAll('[js-comparison="row"]')];
	const bar = container.querySelector('[js-comparison="bar"]');
	const barCells = [...container.querySelectorAll('[js-comparison="barCell"]')];
	const store = container.querySelector('[js-comparison="cardStore"]');
	const picker = container.querySelector('[js-comparison="picker"]');
	const options = [...container.querySelectorAll('[js-comparison-picker="option"]')];
	const familyButtons = [...container.querySelectorAll('[js-comparison-picker="family"]')];
	const removeButton = container.querySelector('[js-comparison-picker="remove"]');

	const columnCount = slotEls.length;
	const desktop = window.matchMedia('(min-width: 990px)');
	const visibleCount = () => (desktop.matches ? columnCount : Math.min(2, columnCount));

	const cards = new Map();
	store.querySelectorAll('[data-entry-id]').forEach((el) => cards.set(el.dataset.entryId, el));

	let slots = new Array(columnCount).fill(null);
	let pickerCol = 0;
	let pickerTrigger = null;

	// ── Rendering ──

	function placeCards() {
		slotEls.forEach((slotEl, col) => {
			const holder = slotEl.querySelector('[js-comparison-slot="card"]');
			const current = holder.firstElementChild;
			const id = slots[col];

			if (current && current.dataset.entryId !== id) store.appendChild(current);
			if (id && cards.has(id) && holder.firstElementChild !== cards.get(id)) holder.appendChild(cards.get(id));

			const entry = id ? byId.get(id) : null;
			slotEl.classList.toggle('product-comparison__slot--empty', !entry);
			slotEl.querySelector('[js-comparison-slot="add"]').hidden = !!entry;
			if (!entry) return;
			const buy = slotEl.querySelector('[js-comparison-slot="buy"]');
			buy.href = entry.product.url;
			// A product Herald marks unavailable shows "Not available yet" instead of the buy label.
			if (entry.product.notAvailableYet || buy.dataset.label) {
				buy.dataset.label ??= buy.textContent;
				buy.textContent = entry.product.notAvailableYet || buy.dataset.label;
			}
			slotEl.querySelector('[js-comparison-slot="label"]').textContent = entry.product.title;
			slotEl.querySelector('[js-comparison-slot="change"]').setAttribute('aria-label', t.change + ': ' + entry.product.title);
		});
	}

	/** Filled, on-screen columns as [col, entry] pairs */
	function visibleEntries() {
		const out = [];
		for (let col = 0; col < visibleCount(); col++) {
			const entry = slots[col] && byId.get(slots[col]);
			if (entry) out.push([col, entry]);
		}
		return out;
	}

	// Stores with a market_config unit system: one unit, in that system, with locale labels.
	// Without one (the UK) the strings stay as they are below.
	const UNIT_KINDS = { range: 'distance', topSpeed: 'speed', power: 'power' };
	function formatUnit(v, kind) {
		const imperial = t.unitSystem === 'imperial' && kind !== 'power';
		const value = imperial ? Math.round(v * 0.621371 * 10) / 10 : v;
		return value.toLocaleString(document.documentElement.lang || undefined) + ' ' + t.units[imperial ? kind + '_imperial' : kind];
	}

	function formatSpec(key, v) {
		if (t.unitSystem && UNIT_KINDS[key]) return formatUnit(v, UNIT_KINDS[key]);
		if (key === 'range') return v + ' km <span class="product-comparison__alt">(' + convertKmToMi(v) + ' mi)</span>';
		if (key === 'power') return v.toLocaleString() + ' W';
		if (key === 'topSpeed') return v + ' km/h <span class="product-comparison__alt">(' + convertKmToMi(v) + ' mph)</span>';
		if (key === 'hillClimbing' || key === 'rideComfort') return scoreBar(v);
		return String(v);
	}

	function renderRows() {
		const visible = visibleEntries();

		rows.forEach((row) => {
			const key = row.dataset.spec;
			const feature = row.dataset.feature;
			const values = slots.map((id) => {
				const entry = id && byId.get(id);
				if (!entry) return null;
				if (feature) return (entry.featureSet || []).includes(feature) ? 1 : 0;
				return isValid(entry[key]) ? entry[key] : -1;
			});

			const shown = visible.map(([col]) => values[col]).filter((v) => v !== null);
			const allSame = shown.length > 1 && shown.every((v) => v === shown[0]);
			row.classList.toggle('product-comparison__spec--same', allSame);

			const ranked = shown.filter((v) => v >= 0);
			const top = ranked.length ? Math.max(...ranked) : null;

			row.querySelectorAll('.product-comparison__cell').forEach((cell) => {
				const col = Number(cell.dataset.col);
				const v = values[col];
				cell.classList.remove('product-comparison__cell--lead', 'product-comparison__cell--none');

				if (v === null) { cell.innerHTML = ''; return; }

				if (feature) {
					cell.innerHTML = v
						? '<span class="product-comparison__value">' + escapeHtml(t.yes) + '</span>'
						: '<span aria-label="' + escapeHtml(t.no) + '">—</span>';
					if (!v) cell.classList.add('product-comparison__cell--none');
					return;
				}

				if (v < 0) {
					cell.innerHTML = '—';
					cell.classList.add('product-comparison__cell--none');
					return;
				}

				cell.innerHTML = '<span class="product-comparison__value">' + formatSpec(key, v) + '</span>';
				if (!allSame && v === top && col < visibleCount()) cell.classList.add('product-comparison__cell--lead');
			});
		});
	}

	function renderBar() {
		barCells.forEach((cell) => {
			const entry = slots[Number(cell.dataset.col)] && byId.get(slots[Number(cell.dataset.col)]);
			if (!entry) { cell.innerHTML = ''; return; }
			// The bar is a visual duplicate of the cards (aria-hidden), so its links stay out of the tab order
			cell.innerHTML =
				'<span class="product-comparison__bar-text">' +
				'<a class="product-comparison__bar-title" tabindex="-1" href="' + escapeHtml(entry.product.url) + '">' + escapeHtml(entry.product.title) + '</a>' +
				'<span class="product-comparison__bar-price">' + escapeHtml(entry.product.priceFormatted) + '</span>' +
				'</span>' +
				'<a class="button" tabindex="-1" href="' + escapeHtml(entry.product.url) + '">' +
				escapeHtml(slotEls[0].querySelector('[js-comparison-slot="buy"]').textContent.trim()) + '</a>';
		});
	}

	function render() {
		placeCards();
		renderRows();
		renderBar();
		updateBar();
	}

	// ── Selection ──

	function firstUnused() {
		const entry = entries.find((e) => !slots.includes(e.id));
		return entry ? entry.id : null;
	}

	/** Put an entry in a column. If it already sits in another (off-screen) column, the two swap. */
	function setSlot(col, id) {
		const other = slots.indexOf(id);
		if (other !== -1 && other !== col) {
			slots[other] = slots[col];
			if (!slots[other]) {
				slots[col] = id;
				slots[other] = firstUnused();
				return;
			}
		}
		slots[col] = id;
	}

	// ── Picker ──

	function filterFamily(family) {
		familyButtons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.family === family)));
		options.forEach((opt) => { opt.hidden = !!family && opt.dataset.family !== family; });
	}

	function openPicker(col, trigger) {
		pickerCol = col;
		pickerTrigger = trigger;
		const onScreen = slots.slice(0, visibleCount());

		options.forEach((opt) => {
			const id = opt.dataset.entryId;
			const isCurrent = slots[col] === id;
			const isTaken = !isCurrent && onScreen.includes(id);
			opt.disabled = isTaken;
			if (isCurrent) opt.setAttribute('aria-current', 'true');
			else opt.removeAttribute('aria-current');
			opt.querySelector('[js-comparison-picker="tag"]').textContent = isCurrent ? t.selected : isTaken ? t.comparing : '';
		});

		const filled = onScreen.filter(Boolean).length;
		removeButton.closest('.product-comparison__dialog-foot').hidden = !slots[col] || filled < 2;

		filterFamily('');
		picker.showDialog();
	}

	function closePicker() {
		picker.closeDialog();
		if (pickerTrigger) pickerTrigger.focus({ preventScroll: true });
	}

	options.forEach((opt) => {
		opt.addEventListener('click', () => {
			const entry = byId.get(opt.dataset.entryId);
			if (!entry || opt.disabled) return;
			setSlot(pickerCol, entry.id);
			render();
			closePicker();

			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: 'selectChangeVariant',
				selectCustomData: [{ product: { handle: entry.product.handle, id: entry.product.id, title: entry.product.title } }],
				selectName: 'picker-' + pickerCol,
				selectValue: entry.id,
			});
		});
	});

	familyButtons.forEach((b) => b.addEventListener('click', () => filterFamily(b.dataset.family)));

	removeButton.addEventListener('click', () => {
		slots[pickerCol] = null;
		render();
		closePicker();
	});

	slotEls.forEach((slotEl, col) => {
		slotEl.querySelectorAll('[js-comparison-slot="change"], [js-comparison-slot="add"]').forEach((btn) => {
			btn.addEventListener('click', () => openPicker(col, btn));
		});
	});

	// ── Toggles ──

	const diffToggle = container.querySelector('[js-comparison="diffToggle"]');
	diffToggle.addEventListener('click', () => {
		const on = diffToggle.getAttribute('aria-pressed') !== 'true';
		diffToggle.setAttribute('aria-pressed', String(on));
		root.classList.toggle('product-comparison--diff-only', on);
	});

	// ── Compact bar: shown once the cards have scrolled under the header ──

	let barRaf = null;
	function updateBar() {
		barRaf = null;
		const anchorTop = parseFloat(getComputedStyle(bar.parentElement).top) || 0;
		const cardsGone = slotsRow.getBoundingClientRect().bottom < anchorTop + bar.offsetHeight;
		const tableLeft = root.getBoundingClientRect().bottom > anchorTop + bar.offsetHeight * 2;
		bar.classList.toggle('product-comparison__bar--visible', cardsGone && tableLeft);
	}
	function scheduleBar() { if (barRaf === null) barRaf = requestAnimationFrame(updateBar); }
	window.addEventListener('scroll', scheduleBar, { passive: true });
	window.addEventListener('resize', scheduleBar);

	// Lead values and "same" rows depend on which columns are on screen
	desktop.addEventListener('change', render);

	// ── Defaults ──
	// ?product1= … ?product4= per column, ?product= / ?variant= (legacy) for column 1,
	// then the section's default products, then the next unused entry.
	const params = new URLSearchParams(window.location.search);

	function findEntry(val) {
		if (!val) return null;
		return entries.find((e) => e.product.handle === val)
			|| byId.get(val)
			|| entries.find((e) => String(e.product.id) === val)
			|| null;
	}

	for (let col = 0; col < columnCount; col++) {
		let entry = findEntry(params.get('product' + (col + 1)));
		if (!entry && col === 0) entry = findEntry(params.get('product') || params.get('variant'));
		if (!entry && Array.isArray(data.defaults)) entry = findEntry(data.defaults[col]);
		if (entry && slots.includes(entry.id)) entry = null;
		slots[col] = entry ? entry.id : firstUnused();
	}

	render();
});
